import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  BillReceiptData,
  Campaign,
  CampaignListParams,
  CampaignListResult,
  CommLogListParams,
  CommLogListResult,
  CommLogsSummary,
  CommLogType,
  CommStats,
  CommTemplate,
  CommTemplateListParams,
  CommTemplateListResult,
  CreateCampaignInput,
  CreateDealInput,
  CreateSocialPostInput,
  CreateTemplateInput,
  Deal,
  DealListResult,
  FeedbackListParams,
  FeedbackListResult,
  FeedbackStats,
  ProductOption,
  SendInvoiceInput,
  SocialPost,
  SocialPostListResult,
  UpdateTemplateInput,
} from './types';

export const BACKEND_GAPS: string[] = [];

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_STATS: CommStats = { messages_today: 0, delivery_rate: 0, open_rate: 0, failed_count: 0, channel_breakdown: [] };
const EMPTY_LOGS_SUMMARY: CommLogsSummary = { total_sent: 0, delivery_rate: 0, read_rate: 0, reorder_clicks: 0 };
const EMPTY_LOG_LIST: CommLogListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_TEMPLATE_LIST: CommTemplateListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_CAMPAIGN_LIST: CampaignListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_DEAL_LIST: DealListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_SOCIAL_LIST: SocialPostListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_FEEDBACK_LIST: FeedbackListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };
const EMPTY_FEEDBACK_STATS: FeedbackStats = { avg_rating: 0, total_reviews: 0, positive_pct: 0, alerts_count: 0 };

export const communicationsApi = {
  // ── Stats ─────────────────────────────────────────────────────────────────
  stats: () => withFallback(() => apiClient<CommStats>('/communications/stats'), EMPTY_STATS),

  // ── Logs ──────────────────────────────────────────────────────────────────
  logs: (params: CommLogListParams) =>
    withFallback(
      () => apiClientPaginated<CommLogListResult['data']>(
        `/communications/logs${buildQuery({
          page: params.page,
          limit: 50,
          channel: params.channel,
          status: params.status,
          type: params.type,
          start_date: params.start_date,
          end_date: params.end_date,
          search: params.search,
        })}`,
      ) as Promise<CommLogListResult>,
      EMPTY_LOG_LIST,
    ),

  logsSummary: (type?: CommLogType) =>
    withFallback(
      () => apiClient<CommLogsSummary>(`/communications/logs/summary${buildQuery({ type })}`),
      EMPTY_LOGS_SUMMARY,
    ),

  // ── Templates ─────────────────────────────────────────────────────────────
  templates: (params: CommTemplateListParams) =>
    withFallback(
      () => apiClientPaginated<CommTemplateListResult['data']>(
        `/communications/templates${buildQuery({ page: params.page, type: params.type })}`,
      ) as Promise<CommTemplateListResult>,
      EMPTY_TEMPLATE_LIST,
    ),

  createTemplate: (input: CreateTemplateInput) =>
    apiClient<CommTemplate>('/communications/templates', { method: 'POST', body: input }),

  updateTemplate: (id: string, input: UpdateTemplateInput) =>
    apiClient<CommTemplate>(`/communications/templates/${id}`, { method: 'PATCH', body: input }),

  deleteTemplate: (id: string) =>
    apiClient<void>(`/communications/templates/${id}`, { method: 'DELETE' }),

  // ── Campaigns ─────────────────────────────────────────────────────────────
  campaigns: (params: CampaignListParams) =>
    withFallback(
      () => apiClientPaginated<CampaignListResult['data']>(
        `/communications/campaigns${buildQuery({ page: params.page, status: params.status })}`,
      ) as Promise<CampaignListResult>,
      EMPTY_CAMPAIGN_LIST,
    ),

  campaign: (id: string) =>
    withFallback(() => apiClient<Campaign>(`/communications/campaigns/${id}`), null),

  createCampaign: (input: CreateCampaignInput) =>
    apiClient<Campaign>('/communications/campaigns', { method: 'POST', body: input }),

  sendCampaign: (id: string) =>
    apiClient<Campaign>(`/communications/campaigns/${id}/send`, { method: 'POST' }),

  deleteCampaign: (id: string) =>
    apiClient<void>(`/communications/campaigns/${id}`, { method: 'DELETE' }),

  // ── Deals ─────────────────────────────────────────────────────────────────
  deals: (page = 1) =>
    withFallback(
      () => apiClientPaginated<DealListResult['data']>(
        `/communications/deals${buildQuery({ page })}`,
      ) as Promise<DealListResult>,
      EMPTY_DEAL_LIST,
    ),

  createDeal: (input: CreateDealInput) =>
    apiClient<Deal>('/communications/deal-of-day', { method: 'POST', body: input }),

  // ── Invoice ───────────────────────────────────────────────────────────────
  sendInvoice: (input: SendInvoiceInput) =>
    apiClient<{ id: string; status: string }>('/communications/send-invoice', { method: 'POST', body: input }),

  // NB: do NOT wrap in withFallback — a 404 here means "bill not found", which
  // the Invoice Preview page surfaces as a dedicated preview-time alert. Swallowing
  // it to `null` makes that alert unreachable (issue #152), so let the error throw.
  billReceipt: (billId: string) =>
    apiClient<BillReceiptData>(`/bills/${billId}/receipt-data`),

  // ── Re-order links (G4.3) ────────────────────────────────────────────────
  createReorderLink: (input: { bill_id: string; channel: 'WHATSAPP' | 'EMAIL'; recipient: string }) =>
    apiClient<{
      id: string;
      bill_id: string;
      reorder_link: string;
      channel: 'WHATSAPP' | 'EMAIL';
      status: string;
      expires_at: string;
    }>('/communications/reorder-link', { method: 'POST', body: input }),

  reorderLinkStatus: (id: string) =>
    apiClient<{
      id: string;
      bill_id: string;
      status: 'QUEUED' | 'CLICKED' | 'EXPIRED' | 'CONVERTED';
      click_count: number;
      last_clicked_at: string | null;
      converted: boolean;
      expires_at: string;
    }>(`/communications/reorder-link/${id}/status`),

  // ── Social Media ──────────────────────────────────────────────────────────
  socialPosts: (page = 1) =>
    withFallback(
      () => apiClientPaginated<SocialPostListResult['data']>(
        `/communications/social-media${buildQuery({ page })}`,
      ) as Promise<SocialPostListResult>,
      EMPTY_SOCIAL_LIST,
    ),

  createSocialPost: (input: CreateSocialPostInput) =>
    apiClient<SocialPost>('/communications/social-media', { method: 'POST', body: input }),

  // ── Feedback ──────────────────────────────────────────────────────────────
  feedback: (params: FeedbackListParams) =>
    withFallback(
      () => apiClientPaginated<FeedbackListResult['data']>(
        `/communications/feedback${buildQuery({
          page: params.page,
          rating: params.rating,
          from: params.from,
          to: params.to,
        })}`,
      ) as Promise<FeedbackListResult>,
      EMPTY_FEEDBACK_LIST,
    ),

  feedbackStats: () =>
    withFallback(() => apiClient<FeedbackStats>('/communications/feedback/stats'), EMPTY_FEEDBACK_STATS),

  markFeedbackRead: (id: string) =>
    apiClient<{ id: string; is_read: boolean }>(`/communications/feedback/${id}/read`, { method: 'PATCH' }),

  // ── Products (for pickers) ────────────────────────────────────────────────
  activeProducts: () =>
    withFallback(
      () => apiClient<ProductOption[]>('/products?is_active=true&limit=50'),
      [],
    ),

  // ── Bills (for the re-order link picker) ──────────────────────────────────
  // Owners/Managers never know raw bill UUIDs — surface recent completed
  // bills so the share modal can offer a searchable bill-number dropdown.
  recentBills: () =>
    withFallback(
      () =>
        apiClientPaginated<
          Array<{
            id: string;
            bill_number: string;
            grand_total: number;
            rounding_adjustment?: number;
            status: string;
            created_at: string;
          }>
        >('/bills?status=COMPLETED&limit=50').then((r) => r.data),
      [],
    ),
};
